/** A design axis: its allowed values and the default value.
 *  Axis `x` maps to the attribute `data-nx-<x>` on the design root. */
export interface DesignAxisDef<V extends string = string> {
  readonly values: readonly V[];
  readonly default: V;
}

export type DesignAxesConfig = Record<string, DesignAxisDef>;

/** Identity helper that pins the literal types of an axis config. */
export function defineDesignAxes<C extends DesignAxesConfig>(config: C): C {
  return config;
}

/** The known design axes. Adding an axis is a deliberate edit here.
 *  (Escape hatch for ad-hoc app keys: setDesignRaw — see useDesign.ts.) */
export const designAxes = defineDesignAxes({
  surface: { values: ['card', 'hairline'], default: 'card' } as const,
  // Input-field treatment. Independent of `surface`: any combination is valid.
  //   filled — resting grey fill, no border
  //   flat   — transparent, no border (border only on hover/focus); disabled
  //            fields drop their fill and border too, their text in the label colour
  //   border — transparent with a resting border
  input: { values: ['filled', 'flat', 'border'], default: 'filled' } as const,
  // (Table zebra striping is no axis: NxTable reads the user's `table.zebra`
  // setting through the kit's settings contract, I-2510.)
  // Functional trailing-icon reveal for inputs. Independent of the other axes.
  //   always — functional (action) icons are always visible (pre-axis behaviour)
  //   hover  — functional icons stay hidden until the field is hovered/focused
  // Consumed by NxInputWrapper, which reads useDesign and ORs this with the
  // per-icon `revealOnInteraction` flag: either one enables reveal. Decorative
  // icons are never affected. The prop can force reveal on, but not off against
  // `hover` (that would need a tri-state) — see NxInputWrapper.
  reveal: { values: ['always', 'hover'], default: 'always' } as const,
  // Input icon COLOUR (independent of `reveal`, which is visibility).
  //   active — icons stay neutral at rest and only the focused field's icons go
  //            brand (the calmer default; brand marks "the field I'm in").
  //   brand  — icons are brand-coloured at rest too (the pre-detox look).
  // Consumed by NxInputWrapper via the --nxd-input-icon-* tokens (design.css).
  inputicon: { values: ['active', 'brand'], default: 'active' } as const,
  // Density: scales the --nxt-space-* family via --nxt-space-scale (mapping in
  // @blpde/nx-tokens base.css). Font sizes are NOT affected — that is the
  // separate font scale on the root font size.
  //   compact — ×0.85 · normal — ×1 · airy — ×1.15
  density: { values: ['compact', 'normal', 'airy'], default: 'normal' } as const,
  // How a field shows its save status (I-993).
  //   slot  — a reserved slot in the trailing icon zone (the established look)
  //   badge — a corner badge on the field's top-right, the same one the
  //           wrapper-less controls use (NxSaveStateBadge, I-422)
  // Like `reveal`, this axis is READ by NxInputWrapper rather than left to the
  // cascade: it swaps markup, not just style. Table edit cells stay on the slot
  // in both values — their trailing zone is an overlay, not chrome.
  savestatus: { values: ['slot', 'badge'], default: 'slot' } as const,
  // Small headings — field labels, field-group titles, panel headings (I-2510).
  //   normal — sentence case, as written
  //   caps   — small capitals: upper case with a little letter spacing; the
  //            group and panel titles also drop to the label size. Every
  //            heading keeps its own weight.
  // Cascade only (--nxd-caption-* tokens, design.css): NxLabel and the apps'
  // group and panel titles read the tokens; nothing branches on the value.
  caps: { values: ['normal', 'caps'], default: 'normal' } as const,
  // Field groups inside a section (I-2510).
  //   card — every group is a card of its own (the surface treatment)
  //   rule — no card: the group sits on the section, its title is an accent
  //          line with a rule below
  // Cascade only (--nxd-group-* tokens, design.css), consumed by the apps'
  // field-group components with today's values as their fallbacks.
  groups: { values: ['card', 'rule'], default: 'card' } as const,
  // The colour of what a record holds and of its headings — input and display
  // values, title cells, record, section and panel titles (I-2510).
  //   neutral — the text colour
  //   brand   — the brand colour itself (the brand fill, as links use it)
  // Cascade only: --nxd-ink (design.css); the kit's inputs read
  // var(--nxd-ink, var(--nxt-input-fg)).
  ink: { values: ['neutral', 'brand'], default: 'neutral' } as const,
  // Lists of a record's functions — the actions of an aside (I-2510).
  //   buttons — every action a bordered button row
  //   list    — plain rows: icon and text, a hover background, no border
  // Cascade only (--nxd-action-* tokens, design.css), consumed by the apps'
  // function panels with today's values as their fallbacks.
  actionlist: { values: ['buttons', 'list'], default: 'buttons' } as const,
  // The shape of badges and chips — NxBadge everywhere (I-2510).
  //   square — small radius with a hairline border
  //   pill   — fully rounded, no border, a little more room at the sides
  // Cascade only (--nxd-badge-* tokens, design.css), read by NxBadge.
  badges: { values: ['square', 'pill'], default: 'square' } as const,
  // The page behind the surfaces (I-2510).
  //   tinted — the neutral page tint, surfaces stand out on it
  //   plain  — the page takes the surface colour: one white sheet in light,
  //            the card colour in dark; hairlines do the separating
  // Cascade only: re-points --nxt-bg-app (design.css, layer nxt.app).
  page: { values: ['tinted', 'plain'], default: 'tinted' } as const,
});

export type DesignAxisName = keyof typeof designAxes;

/** Map of axis name → its current value (a union of that axis's values). */
export type DesignState = {
  [K in DesignAxisName]: (typeof designAxes)[K]['values'][number];
};

/** The data attribute an axis writes to. */
export function axisAttr(axis: string): string {
  return `data-nx-${axis}`;
}

/** Runtime guard: is `value` allowed for `axis`? */
export function isValidAxisValue(axis: string, value: string): boolean {
  const def = (designAxes as DesignAxesConfig)[axis];
  return !!def && (def.values as readonly string[]).includes(value);
}
